/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 04 2024
 **/

import { PropsWithChildren, useCallback, useEffect, useState, useRef } from "react";
import { useShallow } from "zustand/react/shallow";

import { useEventHandlers } from "@rfice/eventhandler";
import { useUnityCommand, useUnityHandler } from "@/frameworks/core/unity";
import { Utility, Configure } from "@/frameworks/libraries";
import { DeviceManager, useDeviceManager } from "@rsupport/devicemanager";
import { LocalData, Properties, useProperties } from "@rsupport/libraries";
import { send_message } from "@/frameworks/core/unity";

import { useModal } from "@/frameworks/designStuff/components";
import { ModalID, ZIndex } from "@/frameworks/core/commons";
import { AllowPermissionModal } from "./private";

export default function DeviceProvider({ children }: PropsWithChildren) {
	const modal = useModal();
	const prevCameraRef = useRef(false);
	const prevMicRef = useRef(false);
	const unityCommand = useUnityCommand();
	const devicemanager = useDeviceManager(
		useShallow((state) => ({
			camera: state.camera,
			microphone: state.microphone,
			speaker: state.speaker
		}))
	);
	const options = useProperties(
		useShallow((state) => ({
			graphic: state.properties[state.selected].graphic_options as { quality: "LOW" | "MEDIUM" | "HIGH" },
			device: state.properties[state.selected].device_options as { video_enable: boolean; audio_enable: boolean }
		}))
	);

	const [enterSpace, setEnterSpace] = useState<boolean>(false);
	const [send_devices_key, set_sent_devices_key] = useState("");
	const [send_graphic_key, set_sent_graphic_key] = useState("");

	const send_option_to_unity = useCallback(
		(manual: boolean = false) => {
			if (Properties.instance.is_global_selected || !options.device || !options.graphic) {
				console.debug("#DeviceProvider#", Properties.instance.selected, JSON.stringify(devicemanager), options);
				return;
			}

			if (!Configure.useUnityLogger) {
				unityCommand.startLogger({ start: false });
			} else {
				unityCommand.startLogger({ start: Configure.useLogLevel > 1 });
			}

			const deviceStatus = {
				...(DeviceManager.instance.is_camera_validate
					? {
							camera: {
								available: (devicemanager.camera.id.length > 0
									? devicemanager.camera.permission === "granted"
										? "YES"
										: "NO"
									: "NONE") as IPC.Devices.Available,
								...(devicemanager.camera.id.length > 0 && {
									state:
										devicemanager.camera.permission === "granted"
											? options.device.video_enable ?? false
											: false,
									deviceID: devicemanager.camera.id,
									label: devicemanager.camera.name
								})
							}
						}
					: {}),
				...(DeviceManager.instance.is_microphone_validate
					? {
							microphone: {
								available: (devicemanager.microphone.id.length > 0
									? devicemanager.microphone.permission === "granted"
										? "YES"
										: "NO"
									: "NONE") as IPC.Devices.Available,
								...(devicemanager.microphone.id.length > 0 && {
									state:
										devicemanager.microphone.permission === "granted"
											? options.device.audio_enable ?? false
											: false,
									deviceID: devicemanager.microphone.id,
									label: devicemanager.microphone.name
								})
							}
						}
					: {})
			};
			const graphicQualityState = { qualityState: options.graphic.quality };

			// 변화가 있을 경우에만 전송함
			const devices_key = Utility.digest(deviceStatus);
			const graphic_key = Utility.digest(graphicQualityState);
			if (manual || devices_key !== send_devices_key) {
				if (Object.keys(deviceStatus).length) {
					// console.debug("#DeviceProvider# send_option_to_unity", deviceStatus);
					send_message({ type: "deviceStatus", payload: deviceStatus });
					set_sent_devices_key(devices_key);
				}
			}
			if (manual || graphic_key !== send_graphic_key) {
				// console.debug("#DeviceProvider# send_option_to_unity", graphicQualityState);
				send_message({ type: "graphicQualityState", payload: graphicQualityState });
				set_sent_graphic_key(graphic_key);
			}
		},
		[devicemanager, options, send_devices_key, send_graphic_key]
	);
	useEventHandlers(
		{
			onSendOptionToUnity() {
				send_option_to_unity(true);
			},
			async onDevicePermission() {
				await DeviceManager.instance.checking_device_permission(true, {
					before_get_media_stream() {
						modal.show({
							id: ModalID.CAMERA_MIC_ALLOW_PERMISSION,
							content: <AllowPermissionModal />,
							zIndex: ZIndex.POPUP + 1
						});
					},
					after_get_media_stream() {
						modal.hide(ModalID.CAMERA_MIC_ALLOW_PERMISSION);
					}
				});
			},
			async onUnityDeviceStatusChanged({ mic, camera }: { mic: boolean; camera: boolean }) {
				if (!enterSpace) return;

				const prevCamera = prevCameraRef.current;
				const prevMic = prevMicRef.current;

				if (devicemanager.camera.permission === "denied" && camera && prevCamera !== camera) {
					modal.show({
						id: ModalID.CAMERA_MIC_ALLOW_PERMISSION,
						content: <AllowPermissionModal />,
						zIndex: ZIndex.POPUP + 1
					});
				}
				if (devicemanager.microphone.permission === "denied" && mic && prevMic !== mic) {
					modal.show({
						id: ModalID.CAMERA_MIC_ALLOW_PERMISSION,
						content: <AllowPermissionModal />,
						zIndex: ZIndex.POPUP + 1
					});
				}

				prevCameraRef.current = camera;
				prevMicRef.current = mic;
			}
		},
		[enterSpace, devicemanager]
	);

	useUnityHandler(
		"enterSpace",
		{
			onEnterSpace: () => {
				setEnterSpace(true);
			}
		},
		[]
	);

	useEffect(() => {
		send_option_to_unity();
	}, [devicemanager, options]);

	useEffect(() => {
		console.debug("#DeviceProvider# initialize", DeviceManager.instance.options, LocalData.get("useLogLevel", "1"));
		DeviceManager.instance.use_debugging = LocalData.get("useLogLevel", "1") === "8";
		DeviceManager.instance.initialize().then(() => {});
	}, []);

	return <>{children}</>;
}
